/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import { apiTokenService } from '../../services/api';
import { APIToken, CreatedAPIToken, APITokenExpiry } from '../../types';
import { Button, Skeleton, Tag } from '../console/ui';
import SettingsDialog from './SettingsDialog';

const MAX_TOKENS = 5;

const ApiKeysSection: React.FC = () => {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<APIToken | null>(null);
  const [createdToken, setCreatedToken] = useState<CreatedAPIToken | null>(null);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({ name: '', expires_in: '30d' as APITokenExpiry });

  const { data: tokensData, isLoading } = useQuery({
    queryKey: ['api-tokens'],
    queryFn: () => apiTokenService.list().then((res) => res.data),
  });

  const tokens: APIToken[] = Array.isArray(tokensData) ? tokensData : tokensData?.data || [];
  const canCreateMore = tokens.length < MAX_TOKENS;

  const resetForm = () => setFormData({ name: '', expires_in: '30d' });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; expiresIn: string }) => apiTokenService.create(data.name, data.expiresIn),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
      setCreatedToken(response.data);
      setShowCreate(false);
      toast.success('API key created');
    },
    onError: (error: any) => {
      if (error.response?.status === 402 && error.response?.data?.error === 'api_token_limit_reached') {
        toast.error(`Maximum of ${MAX_TOKENS} API keys allowed. Revoke an existing key first.`);
      } else {
        toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to create API key');
      }
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => apiTokenService.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
      toast.success('API key revoked');
      setTokenToRevoke(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to revoke API key');
    },
  });

  const copyToken = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('API key copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const closeCreated = () => {
    setCreatedToken(null);
    setCopied(false);
    resetForm();
  };

  return (
    <section className="ps-card p-5 flex flex-col gap-3.5" aria-labelledby="api-keys-heading">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 id="api-keys-heading" className="m-0 text-lg font-medium text-ps-text">
            API keys
          </h2>
          <p className="m-0 mt-1 text-ps-muted text-sm">
            One key works for Inference, the podstack CLI and the REST API.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-ps-subtle text-[13px]">
            {tokens.length}/{MAX_TOKENS} keys
          </span>
          <Button onClick={() => setShowCreate(true)} disabled={!canCreateMore}>
            Create API key
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Tag tone="info">Inference</Tag>
        <Tag tone="info">podstack CLI</Tag>
        <Tag tone="info">REST API</Tag>
      </div>

      {isLoading ? (
        <Skeleton className="h-24" />
      ) : tokens.length === 0 ? (
        <div className="p-5 text-center text-ps-muted text-sm border border-dashed border-ps-strong rounded-[10px]">
          No API keys yet. Create one to authenticate the CLI, the REST API or Inference requests.
        </div>
      ) : (
        <div className="overflow-x-auto border border-ps-border rounded-[10px]">
          <table className="ps-tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Last used</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((token) => (
                <tr key={token.id}>
                  <td className="text-ps-text">
                    {token.name}
                    {token.is_expired && (
                      <Tag tone="err" className="ml-2">
                        Expired
                      </Tag>
                    )}
                  </td>
                  <td className="font-mono text-xs text-ps-muted">{token.prefix}…</td>
                  <td className="text-ps-muted">{format(new Date(token.created_at), 'd MMM yyyy')}</td>
                  <td className={token.is_expired ? 'text-ps-err' : 'text-ps-muted'}>
                    {token.expires_at ? format(new Date(token.expires_at), 'd MMM yyyy') : 'Never'}
                  </td>
                  <td className="text-ps-muted">
                    {token.last_used_at ? formatDistanceToNow(new Date(token.last_used_at), { addSuffix: true }) : 'Never'}
                  </td>
                  <td className="text-right">
                    <Button variant="secondary" size="sm" onClick={() => setTokenToRevoke(token)}>
                      Revoke
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <SettingsDialog
          title="Create API key"
          onClose={() => {
            setShowCreate(false);
            resetForm();
          }}
        >
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              createMutation.mutate({ name: formData.name, expiresIn: formData.expires_in });
            }}
          >
            <div>
              <label className="ps-label" htmlFor="tok-name">
                Name
              </label>
              <input
                id="tok-name"
                className="ps-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. laptop-cli"
                required
                maxLength={100}
              />
            </div>
            <div>
              <label className="ps-label" htmlFor="tok-exp">
                Expires
              </label>
              <select
                id="tok-exp"
                className="ps-input"
                value={formData.expires_in}
                onChange={(e) => setFormData({ ...formData, expires_in: e.target.value as APITokenExpiry })}
              >
                <option value="7d">7 days</option>
                <option value="30d">30 days</option>
                <option value="90d">90 days</option>
                <option value="180d">6 months</option>
                <option value="365d">1 year</option>
                <option value="never">Never</option>
              </select>
            </div>
            <div className="flex flex-wrap justify-end gap-2.5 pt-1">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCreate(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating…' : 'Create API key'}
              </Button>
            </div>
          </form>
        </SettingsDialog>
      )}

      {createdToken && (
        <SettingsDialog title="Copy your API key" onClose={closeCreated}>
          <p className="m-0 px-3.5 py-3 rounded-[10px] bg-ps-warn-bg text-ps-warn text-sm">
            This is the only time the full key is shown. Copy it and store it somewhere safe.
          </p>
          <div>
            <label className="ps-label" htmlFor="tok-value">
              API key
            </label>
            <div className="flex gap-2">
              <input id="tok-value" readOnly value={createdToken.token} className="ps-input font-mono text-xs" />
              <Button variant="secondary" onClick={() => copyToken(createdToken.token)}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-1 text-sm text-ps-muted">
            <span>
              Name: <span className="text-ps-text">{createdToken.name}</span>
            </span>
            <span>
              Expires:{' '}
              <span className="text-ps-text">
                {createdToken.expires_at ? format(new Date(createdToken.expires_at), 'd MMMM yyyy') : 'Never'}
              </span>
            </span>
            <span className="text-ps-subtle text-[13px] mt-1">
              For JupyterHub, enter any username and paste this key as the password.
            </span>
          </div>
          <Button onClick={closeCreated}>I’ve saved my key</Button>
        </SettingsDialog>
      )}

      {tokenToRevoke && (
        <SettingsDialog title="Revoke API key" size="md" onClose={() => setTokenToRevoke(null)}>
          <p className="m-0 text-sm text-ps-muted">
            Revoke <span className="text-ps-text font-semibold">“{tokenToRevoke.name}”</span>? Anything using this key will stop
            authenticating. This can’t be undone.
          </p>
          <div className="flex flex-wrap justify-end gap-2.5">
            <Button variant="secondary" onClick={() => setTokenToRevoke(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => revokeMutation.mutate(tokenToRevoke.id)} disabled={revokeMutation.isPending}>
              {revokeMutation.isPending ? 'Revoking…' : 'Revoke key'}
            </Button>
          </div>
        </SettingsDialog>
      )}
    </section>
  );
};

export default ApiKeysSection;
